import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  static #quantityIntents = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    let lineKey = this.refs.cartItemRows[line - 1]?.dataset.key;
    const intent = this.#rememberQuantityIntent(lineKey, line, quantity);
    this.#restoreQuantityIntents();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId && sectionsToUpdate.size < 5) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionIds = Array.from(sectionsToUpdate);

    const payload = {
      quantity: quantity,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
    if (lineKey) {
      payload.id = lineKey;
    } else {
      payload.line = line;
    }
    cartTotal?.shimmer();

    const request = async () => {
      try {
        if (window.BionoboCartMutations?.prepareMutation) await window.BionoboCartMutations.prepareMutation({ lineKey });
        if (lineKey) {
          lineKey = window.BionoboCartMutations?.resolveLineKey?.(lineKey) || lineKey;
          payload.id = lineKey;
        }
        const body = JSON.stringify(payload);
        let response;
        try {
          response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        } catch (error) {
          const uncertainError = error instanceof Error ? error : new Error('The cart could not be updated.');
          uncertainError.cartMutationUncertain = true;
          throw uncertainError;
        }
        const responseText = await response.text();
        let parsedResponseText;
        try {
          parsedResponseText = JSON.parse(responseText);
        } catch {
          const error = new Error('The cart response could not be read.');
          error.cartMutationUncertain = true;
          throw error;
        }

        if (!response.ok || parsedResponseText.status || parsedResponseText.errors) {
          const message = parsedResponseText.description || parsedResponseText.message || parsedResponseText.errors;
          const error = new Error(message || 'The cart could not be updated.');
          error.cartMutationUncertain = response.status !== 422
            && Number(parsedResponseText.status) !== 422
            && !parsedResponseText.errors;
          throw error;
        }

        window.BionoboCartMutations?.invalidateRead?.(parsedResponseText);
        if (window.BionoboCartMutations?.reconcile) {
          parsedResponseText = await window.BionoboCartMutations.reconcile(parsedResponseText);
        }

        for (const sectionId of sectionIds) {
          if (typeof parsedResponseText.sections?.[sectionId] !== 'string' || !parsedResponseText.sections[sectionId].trim()) {
            const error = new Error('The cart changed, but its sections could not be refreshed.');
            error.cartMutationUncertain = true;
            throw error;
          }
        }

        resetShimmer(this);
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);
        const ownSettlement = Promise.resolve().then(() => morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          this.isDrawer ? 'hydration' : 'full'
        ));
        const cartUpdate = new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            itemCountIsTotal: true,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            sectionSettlements: [ownSettlement],
            quantitySettlements: [],
          });
        this.dispatchEvent(cartUpdate);

        await (window.BionoboCartMutations?.settleEvent?.(cartUpdate)
          || Promise.all(cartUpdate.detail.data.sectionSettlements));
        this.#updateCartQuantitySelectorButtonStates();
        this.#restoreQuantityIntents();
        if (config.action === 'clear') this.#focusAfterRemoval(line - 1);
        return true;
      } catch (error) {
        window.BionoboCartMutations?.invalidateRead?.();
        resetShimmer(this);
        const message = error instanceof Error && error.message ? error.message : 'The cart could not be updated.';
        const recoveredSections = {};
        const refreshResults = await Promise.allSettled(
          sectionIds.map(async (sectionId) => {
            const html = await sectionRenderer.renderSection(sectionId, { cache: false });
            if (typeof html === 'string' && html.trim()) recoveredSections[sectionId] = html;
            return html;
          })
        );
        const ownSectionIndex = sectionIds.indexOf(this.sectionId);
        const ownSectionRecovered = ownSectionIndex >= 0
          && refreshResults[ownSectionIndex]?.status === 'fulfilled'
          && typeof recoveredSections[this.sectionId] === 'string';
        if (error?.cartMutationUncertain !== false
          && (!ownSectionRecovered || refreshResults.some((result) => result.status === 'rejected'))) {
          window.BionoboCartMutations?.requireFresh?.();
        }

        if (!error?.giftReconciliationFailed && error?.cartMutationUncertain !== false && ownSectionRecovered) {
          const currentComponent = document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) || this;
          const rows = currentComponent.refs?.cartItemRows || [];
          const matchingIndex = lineKey
            ? Array.from(rows).findIndex((row) => row.dataset?.key === lineKey)
            : line - 1;
          const matchingSelector = matchingIndex >= 0 ? currentComponent.refs?.quantitySelectors?.[matchingIndex] : null;
          const matchingInput = matchingSelector?.querySelector('input');
          const authoritativeQuantity = Number(matchingInput?.defaultValue ?? matchingInput?.value);
          const mutationApplied = matchingIndex < 0 || authoritativeQuantity === quantity;

          if (mutationApplied) {
            const sectionDocument = new DOMParser().parseFromString(recoveredSections[this.sectionId], 'text/html');
            const itemCountText = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent;
            const itemCount = Number(itemCountText);
            document.dispatchEvent(
              new CartUpdateEvent({}, this.sectionId, {
                itemCount: Number.isFinite(itemCount) ? itemCount : 0,
                itemCountIsTotal: true,
                source: 'cart-items-component',
                sections: recoveredSections,
              })
            );
            if (config.action === 'clear') this.#focusAfterRemoval(line - 1);
            return true;
          }
        }

        this.#showCartError(lineKey, line, message);
        return false;
      } finally {
        intent.pending -= 1;
        if (intent.pending === 0) CartItemsComponent.#quantityIntents.delete(intent.key);
        window.BionoboCartMutations?.restoreNotice?.();
        this.#enableCartItems();
        this.#restoreQuantityIntents();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      }
    };
    const mutationQueue = window.BionoboCartMutations?.enqueue;
    const pendingRequest = mutationQueue ? mutationQueue(request) : request();
    return pendingRequest;
  }

  #quantityIntent(lineKey, sectionId, line) {
    const identity = lineKey
      ? window.BionoboCartMutations?.resolveLineKey?.(lineKey) || lineKey
      : `${sectionId}:${line}`;
    for (const intent of CartItemsComponent.#quantityIntents.values()) {
      const currentIdentity = intent.lineKey
        ? window.BionoboCartMutations?.resolveLineKey?.(intent.lineKey) || intent.lineKey
        : intent.key;
      if (currentIdentity === identity) return intent;
    }
    return null;
  }

  #rememberQuantityIntent(lineKey, line, quantity) {
    let intent = this.#quantityIntent(lineKey, this.sectionId, line);
    if (!intent) {
      const key = lineKey || `${this.sectionId}:${line}`;
      intent = { key, lineKey, quantity, pending: 0 };
      CartItemsComponent.#quantityIntents.set(key, intent);
    }
    intent.quantity = quantity;
    intent.pending += 1;
    return intent;
  }

  #restoreQuantityIntents() {
    for (const component of document.querySelectorAll('cart-items-component')) {
      let updating = false;
      const rows = component.refs?.cartItemRows || [];
      Array.from(rows).forEach((row, index) => {
        const intent = this.#quantityIntent(row.dataset?.key, component.dataset.sectionId, index + 1);
        if (intent?.quantity === 0) {
          row.dataset.bnPendingRemoval = 'true';
          row.inert = true;
        } else if (row.dataset.bnPendingRemoval) {
          delete row.dataset.bnPendingRemoval;
          row.inert = false;
        }
        if (!intent) {
          component.refs?.quantitySelectors?.[index]?.updateButtonStates?.();
          return;
        }
        updating = true;
        const selector = component.refs?.quantitySelectors?.[index];
        const input = selector?.querySelector('input');
        if (input) input.value = String(intent.quantity);
        selector?.updateButtonStates?.();
      });
      component.ariaBusy = updating ? 'true' : null;
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {string | undefined} lineKey - The stable Shopify line key.
   * @param {number} fallbackLine - The original line when no key was rendered.
   * @param {string} message - The shopper-facing error.
   */
  #showCartError = (lineKey, fallbackLine, message) => {
    const currentComponent = document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) || this;
    const refs = currentComponent.refs || this.refs;
    const rows = refs.cartItemRows || [];
    const matchingIndex = lineKey
      ? Array.from(rows).findIndex((row) => row.dataset?.key === lineKey)
      : fallbackLine - 1;
    if (matchingIndex < 0) return;
    const line = matchingIndex + 1;
    const quantitySelector = refs.quantitySelectors?.[matchingIndex];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = refs[`cartItemError-${line}`];
    const cartItemErrorContainer = refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement) cartItemError.textContent = message;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');
  };

  /** @param {number} removedIndex */
  #focusAfterRemoval(removedIndex) {
    queueMicrotask(() => {
      const currentComponent = document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`);
      if (!(currentComponent instanceof HTMLElement)) return;
      const rows = currentComponent.refs?.cartItemRows || [];
      const nextRow = rows[Math.min(removedIndex, rows.length - 1)];
      const target = nextRow?.querySelector('[name="minus"], input, button, a')
        || currentComponent.querySelector('.cart-items__empty-button, [ref="closeButton"], button, a');
      target?.focus();
    });
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const settlement = Promise.resolve()
        .then(() => morphSection(this.sectionId, cartItemsHtml))
        .then(() => {
          this.#updateCartQuantitySelectorButtonStates();
          this.#restoreQuantityIntents();
        });
      const sectionSettlements = event.detail.data.sectionSettlements;
      if (Array.isArray(sectionSettlements)) sectionSettlements.push(settlement);
      settlement
        .catch(() => sectionRenderer.renderSection(this.sectionId, { cache: false }))
        .catch((error) => console.error(error));
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Marks cart items as updating while leaving quantity gestures available.
   */
  #disableCartItems() {
    this.ariaBusy = 'true';
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.ariaBusy = null;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
